import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { LayoutGrid, X } from "lucide-react";

const TABS = [
  { id: "all", label: "All Masterpieces" },
  { id: "groom", label: "Groom Styling" },
  { id: "personal", label: "Personal Couture" },
  { id: "vests", label: "Statement Vests" },
];

const LOOKS = [
  {
    id: "groom-1",
    category: "groom",
    title: "Midnight Reception Suit",
    img: "https://customer-assets-4nw71qhi.emergentagent.net/job_mrveer-styling/artifacts/83uqbsib_IMG_9345_Original.webp",
    fabric: "Midnight navy double-breasted suit",
    draping: "Styled with maroon saafa & gold-toe mojaris",
    palette: "Navy, maroon & gold",
    event: "Reception / Cocktail",
  },
  {
    id: "groom-2",
    category: "groom",
    title: "Anand Karaj Kalgi Ceremony",
    img: "https://customer-assets-4nw71qhi.emergentagent.net/job_mrveer-styling/artifacts/o2ux45xb_IMG_1218_Original.webp",
    fabric: "Ivory threadwork sherwani",
    draping: "Maroon saafa with feather kalgi & layered ruby mala",
    palette: "Ivory, maroon & gold",
    event: "Anand Karaj / Wedding Day",
  },
  {
    id: "personal-1",
    category: "personal",
    title: "Houndstooth Cocktail Look",
    img: "https://customer-assets-4nw71qhi.emergentagent.net/job_mrveer-styling/artifacts/ljauy7yk_7ABCC9FD-77ED-484E-97F3-0194936D1D0C.PNG",
    fabric: "Houndstooth blazer over black shirt & trousers",
    draping: "Blush pink tie paired with a purple saafa",
    palette: "Black, blush pink & purple",
    event: "Cocktail / Sangeet Night",
  },
  {
    id: "vest-1",
    category: "vests",
    title: "Olive Field Vest",
    img: "https://customer-assets-4nw71qhi.emergentagent.net/job_mrveer-styling/artifacts/dy1wcpx9_Image%205.jpeg",
    fabric: "Olive nubuck leather, gunmetal fittings",
    draping: "Utility drape over a black bandhgala",
    palette: "Olive & gunmetal",
    event: "Sangeet / Festive Evening",
  },
  {
    id: "vest-2",
    category: "vests",
    title: "Ivory & Tan Two-Tone Vest",
    img: "https://customer-assets-4nw71qhi.emergentagent.net/job_mrveer-styling/artifacts/o2e2fl4p_Image%206.jpeg",
    fabric: "Ivory suede body with tan leather trims",
    draping: "Statement layer over cream day ensembles",
    palette: "Ivory, tan & brass",
    event: "Day Wedding / Haldi",
  },
  {
    id: "vest-3",
    category: "vests",
    title: "Cocoa Zip Utility Vest",
    img: "https://customer-assets-4nw71qhi.emergentagent.net/job_mrveer-styling/artifacts/7taz5mel_Image%207.jpeg",
    fabric: "Distressed cocoa leather with zip hardware",
    draping: "Zip-front rig over a monochrome kurta set",
    palette: "Deep brown & charcoal",
    event: "Cocktail / Reception",
  },
  {
    id: "vest-4",
    category: "vests",
    title: "Tan Expedition Vest",
    img: "https://customer-assets-4nw71qhi.emergentagent.net/job_mrveer-styling/artifacts/88oss5nx_Image%208.jpeg",
    fabric: "Full-grain tan leather, antique brass buckles",
    draping: "Layered over a bandhgala or plain silk kurta",
    palette: "Tan, brass & cream",
    event: "Sangeet / Mehendi",
  },
];

const EASE = [0.16, 1, 0.3, 1];

export default function Portfolio() {
  const [tab, setTab] = useState("all");
  const [active, setActive] = useState(null);
  const [viewAll, setViewAll] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });

  useEffect(() => {
    if (!emblaApi) return undefined;
    const id = setInterval(() => emblaApi.scrollNext(), 2000);
    return () => clearInterval(id);
  }, [emblaApi]);

  const looks = tab === "all" ? LOOKS : LOOKS.filter((l) => l.category === tab);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="bg-[#FDFBF7] py-24 lg:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-16 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-xs font-semibold uppercase tracking-[0.35em] text-[#C5A059] lg:col-span-3"
          >
            Styled Outfits
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="font-serif text-3xl font-medium leading-tight tracking-tight text-[#141414] sm:text-4xl lg:col-span-9 lg:text-5xl"
          >
            Client Masterpieces —{" "}
            <span className="italic text-[#C5A059]">every look tells a story.</span>
          </motion.h2>
        </div>

        <div className="mb-14 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3" data-testid="portfolio-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`portfolio-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  tab === t.id
                    ? "text-[#0C0C0C]"
                    : "border border-[#E5DEC9] text-[#686152] hover:border-[#C5A059]/60 hover:text-[#141414]"
                }`}
              >
                {tab === t.id && (
                  <motion.span
                    layoutId="tab-pill"
                    className="absolute inset-0 rounded-full bg-[#C5A059]"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{t.label}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="portfolio-view-all-btn"
              onClick={() => setViewAll(!viewAll)}
              className="flex items-center gap-2 rounded-full border border-[#141414]/25 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#141414] transition-colors duration-300 hover:border-[#C5A059] hover:text-[#B38C43]"
            >
              <LayoutGrid size={14} />
              {viewAll ? "Slideshow" : "View All"}
            </button>
          </div>
        </div>

        {viewAll ? (
          <div
            data-testid="portfolio-grid"
            className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
          >
            {looks.map((look) => (
              <button
                key={look.id}
                onClick={() => setActive(look)}
                onMouseMove={onMove}
                data-testid={`grid-card-${look.id}`}
                className="spotlight-card group relative block w-full overflow-hidden border border-[#2A2A2A] bg-[#181818] text-left"
              >
                <div className="overflow-hidden">
                  <img
                    src={look.img}
                    alt={look.title}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover grayscale-[25%] transition-all duration-700 group-hover:scale-[1.04] group-hover:grayscale-0"
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0C0C0C] via-[#0C0C0C]/60 to-transparent p-6 pt-16">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">
                    {TABS.find((t) => t.id === look.category)?.label}
                  </p>
                  <h3 className="mt-1 font-serif text-xl text-[#FDFBF7] sm:text-2xl">
                    {look.title}
                  </h3>
                </div>
              </button>
            ))}
          </div>
        ) : (
        <div
          key={tab}
          ref={emblaRef}
          data-testid="portfolio-carousel"
          className="overflow-hidden"
        >
          <div className="flex">
            {looks.map((look) => (
              <div
                key={look.id}
                className="min-w-0 flex-[0_0_100%] pr-8 sm:flex-[0_0_50%] lg:flex-[0_0_33.3333%]"
              >
                <button
                  onClick={() => setActive(look)}
                  onMouseMove={onMove}
                  data-testid={`look-card-${look.id}`}
                  className="spotlight-card group relative block w-full overflow-hidden border border-[#2A2A2A] bg-[#181818] text-left"
                >
                  <div className="overflow-hidden">
                    <img
                      src={look.img}
                      alt={look.title}
                      loading="lazy"
                      className="aspect-[3/4] w-full object-cover grayscale-[25%] transition-all duration-700 group-hover:scale-[1.04] group-hover:grayscale-0"
                    />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0C0C0C] via-[#0C0C0C]/60 to-transparent p-6 pt-16">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">
                      {TABS.find((t) => t.id === look.category)?.label}
                    </p>
                    <h3 className="mt-1 font-serif text-xl text-[#FDFBF7] sm:text-2xl">
                      {look.title}
                    </h3>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="outfit-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0C0C0C]/80 p-4 backdrop-blur-md"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.45, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="grid max-h-[90vh] w-full max-w-4xl grid-cols-1 overflow-y-auto border border-[#E5DEC9] bg-[#FDFBF7] md:grid-cols-2"
            >
              <img
                src={active.img}
                alt={active.title}
                className="h-64 w-full object-cover md:h-full"
              />
              <div className="relative p-8 lg:p-12">
                <button
                  data-testid="outfit-modal-close"
                  onClick={() => setActive(null)}
                  aria-label="Close details"
                  className="absolute right-5 top-5 text-[#686152] transition-colors hover:text-[#B38C43]"
                >
                  <X size={22} />
                </button>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">
                  {TABS.find((t) => t.id === active.category)?.label}
                </p>
                <h3 className="mt-2 font-serif text-2xl text-[#141414] sm:text-3xl">
                  {active.title}
                </h3>
                <dl className="mt-8 space-y-5">
                  {[
                    ["Fabric", active.fabric],
                    ["Draping", active.draping],
                    ["Colour Story", active.palette],
                    ["Event", active.event],
                  ].map(([k, v]) => (
                    <div key={k} className="border-b border-[#E5DEC9] pb-4">
                      <dt className="text-[10px] uppercase tracking-[0.28em] text-[#686152]">
                        {k}
                      </dt>
                      <dd className="mt-1 text-sm text-[#141414]">{v}</dd>
                    </div>
                  ))}
                </dl>
                <a
                  data-testid="outfit-modal-whatsapp"
                  href={`https://wa.me/918801387013?text=${encodeURIComponent(
                    `Hello Manvir, I would love a look like "${active.title}".`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-block rounded-full bg-[#C5A059] px-7 py-3 text-xs font-bold uppercase tracking-[0.2em] text-[#0C0C0C] transition-colors hover:bg-[#B38C43]"
                >
                  I Want This Look
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
